import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IApiError } from '@core/interfaces/api-error.interface';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { ITeam } from '@core/interfaces/team.interface';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { TeamService } from '@core/services/team.service';
import { ComponentStore } from '@ngrx/component-store';
import { TranslateService } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { ICustomer } from '@pages/customers/customers.interface';
import * as Sentry from '@sentry/angular';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { EMPTY, MonoTypeOperatorFunction, Observable, pipe, TimeoutError } from 'rxjs';
import { catchError, mergeMap, switchMap, tap, timeout } from 'rxjs/operators';
import { TCampaignApi, TCampaignCreatePayload, TCampaignUpdatePayload } from '../campaigns.interface';
import { CampaignsService } from '../campaigns.service';
import { CampaignDatesService } from './form-fields/campaign-basics/campaign-dates/campaign-dates.service';

interface ICampaignFormState {
  campaign: TCampaignApi | null;
  campaignWithCustomerLoading: boolean;
  campaignWithAllLoading: boolean;
  campaignWithAllLoaded: boolean;
  campaignSubmitLoading: boolean;
  creators: ICreator[] | null;
  creatorsLoading: boolean;
  customers: ICustomer[] | null;
  customersLoading: boolean;
  teams: ITeam[] | null;
  teamsLoading: boolean;
  error: string | null;
}

const initialState: ICampaignFormState = {
  campaign: null,
  campaignSubmitLoading: false,
  campaignWithAllLoaded: false,
  campaignWithAllLoading: false,
  campaignWithCustomerLoading: false,
  creators: null,
  creatorsLoading: false,
  customers: null,
  customersLoading: false,
  error: null,
  teams: null,
  teamsLoading: false,
};

@Injectable()
export class CampaignFormStore extends ComponentStore<ICampaignFormState> {
  public readonly campaign$ = this.selectSignal((state: ICampaignFormState) => state.campaign);
  public readonly campaignLoading$ = this.selectSignal(
    (state: ICampaignFormState) => state.campaignWithCustomerLoading,
  );
  public readonly creators$ = this.selectSignal((state: ICampaignFormState) => state.creators);
  public readonly customers$ = this.selectSignal((state: ICampaignFormState) => state.customers);
  public readonly creatorsLoading$ = this.selectSignal((state: ICampaignFormState) => state.creatorsLoading);
  public readonly customersLoading$ = this.selectSignal((state: ICampaignFormState) => state.customersLoading);
  public readonly teams$ = this.selectSignal((state: ICampaignFormState) => state.teams);
  public readonly teamsLoading$ = this.selectSignal((state: ICampaignFormState) => state.teamsLoading);
  public readonly error$ = this.selectSignal((state: ICampaignFormState) => state.error);
  public readonly campaignSubmitLoading$ = this.selectSignal(
    (state: ICampaignFormState) => state.campaignSubmitLoading,
  );
  public readonly campaignWithAllLoading$ = this.selectSignal(
    (state: ICampaignFormState) => state.campaignWithAllLoading,
  );
  public readonly campaignWithAllLoaded$ = this.selectSignal(
    (state: ICampaignFormState) => state.campaignWithAllLoaded,
  );
  public readonly campaignWithCustomerLoading$ = this.selectSignal(
    (state: ICampaignFormState) => state.campaignWithCustomerLoading,
  );

  private readonly MAX_CONCURRENT_CAMPAIGNS_ERROR_CODE = 'MAX_CONCURRENT_CAMPAIGNS_REACHED';

  private readonly errorLoadResetValues = {
    campaignSubmitLoading: false,
    campaignWithAllLoading: false,
    campaignWithCustomerLoading: false,
    creatorsLoading: false,
    customersLoading: false,
    teamsLoading: false,
  } as const;

  private handleSubmitError(
    err: unknown,
    options: { campaignId?: string; operation: string; payload: unknown; snackbarKey: string; timeoutMessage: string },
  ): typeof EMPTY {
    const isTimeout = err instanceof TimeoutError;

    Sentry.withScope((scope) => {
      scope.setTag('operation', options.operation);
      scope.setExtra('isTimeout', isTimeout);
      scope.setExtra('payload', options.payload);

      if (options.campaignId) {
        scope.setExtra('campaignId', options.campaignId);
      }

      if (err instanceof HttpErrorResponse) {
        scope.setExtra('errorBody', JSON.stringify(err.error));
        scope.setExtra('errorDetails', JSON.stringify((err.error as IApiError | undefined)?.details));
      }

      const exception = isTimeout
        ? new Error(options.timeoutMessage)
        : err instanceof Error
          ? err
          : new Error(
              err instanceof HttpErrorResponse ? `HTTP ${err.status}: ${err.message}` : 'Campaign submit failed',
            );

      Sentry.captureException(exception);
    });

    this.patchState({ ...this.errorLoadResetValues, error: 'Failed to submit campaign' });

    // ApiInterceptor already shows the specific limit message; a generic snackbar would replace it.
    if (!this.isConcurrentCampaignLimitError(err)) {
      this.snackbarService.openError(this.translate.instant(options.snackbarKey));
    }

    return EMPTY;
  }

  private isConcurrentCampaignLimitError(err: unknown): boolean {
    return (
      err instanceof HttpErrorResponse &&
      this.errorFormatterService
        .getErrorDetails(err.error as IApiError)
        .some((detail) => detail.code === this.MAX_CONCURRENT_CAMPAIGNS_ERROR_CODE)
    );
  }

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          ...this.errorLoadResetValues,
          error: errorMessage,
        });

        return EMPTY;
      }),
    );

  public readonly fetchCreators = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ creatorsLoading: true, error: null })),
      switchMap(() =>
        this.campaignsService
          .getCreators(new HttpParams().set('limit', '1000').set('order_by', '-created_at').set('joins', 'channels'))
          .pipe(
            tap((creators: IApiResponse<ICreator[]>) => {
              this.patchState({ creators: creators.data ?? [], creatorsLoading: false });
            }),
            this.handleError('Failed to load creators'),
          ),
      ),
    );
  });

  public readonly fetchCustomers = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ customersLoading: true, error: null })),
      switchMap(() =>
        this.campaignsService.getCustomers(new HttpParams().set('limit', '1000').set('order_by', '-created_at')).pipe(
          tap((customers: IApiResponse<ICustomer[]>) => {
            this.patchState({ customers: customers.data ?? [], customersLoading: false });
          }),
          this.handleError('Failed to load customers'),
        ),
      ),
    );
  });

  public readonly fetchTeams = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, teamsLoading: true })),
      switchMap(() =>
        this.teamService.getTeams(new HttpParams().set('limit', '1000')).pipe(
          tap((teams: IApiResponse<ITeam[]>) => {
            this.patchState({ teams: teams.data ?? [], teamsLoading: false });
          }),
          this.handleError('Failed to load teams'),
        ),
      ),
    );
  });

  /** Merges a just-created team into `teams` so every consumer of `teams$()` sees it immediately -
   * not just CampaignTeamSelectComponent (the "Team" field that created it via its inline "Add
   * team" dialog), but also its sibling CampaignTeamComponent (the "Visibility" whitelist), which
   * reads `teams$()` directly rather than through CampaignTeamSelectComponent's own local
   * newTeams/teams merge. Dedupes by id defensively, though a freshly created team can't already
   * be present. */
  public upsertTeam(team: ITeam): void {
    const currentTeams = this.teams$() ?? [];

    this.patchState({ teams: [team, ...currentTeams.filter((existingTeam) => existingTeam.id !== team.id)] });
  }

  public readonly fetchCampaign = this.effect(
    (trigger$: Observable<{ adminMode: boolean; campaignId: string; joins: 'customer,whitelist,team' | 'all' }>) => {
      return trigger$.pipe(
        mergeMap(({ adminMode, campaignId, joins }) => {
          this.patchState({
            [joins === 'all' ? 'campaignWithAllLoading' : 'campaignWithCustomerLoading']: true,
            error: null,
          });

          const params = new HttpParams().set('joins', joins);
          const fetch$ = adminMode
            ? this.campaignsService.getAdminCampaign(campaignId, params)
            : this.campaignsService.getCampaign(campaignId, params);

          return fetch$.pipe(
            tap((campaign: TCampaignApi | undefined) => {
              if (campaign) {
                const isSupersededByAllFetch = joins !== 'all' && this.get().campaignWithAllLoaded;

                this.patchState({
                  ...(isSupersededByAllFetch ? {} : { campaign }),
                  [joins === 'all' ? 'campaignWithAllLoading' : 'campaignWithCustomerLoading']: false,
                  ...(joins === 'all' ? { campaignWithAllLoaded: true } : {}),
                });
              } else {
                this.patchState({
                  [joins === 'all' ? 'campaignWithAllLoading' : 'campaignWithCustomerLoading']: false,
                  error: 'Campaign not found',
                });
              }
            }),
            this.handleError('Failed to load campaign'),
          );
        }),
      );
    },
  );

  public readonly createCampaign = this.effect((trigger$: Observable<TCampaignCreatePayload>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ campaignSubmitLoading: true, error: null })),
      switchMap((campaign: TCampaignCreatePayload) =>
        this.campaignsService.createCampaign(campaign).pipe(
          timeout(60_000),
          tap((campaignResponse: TCampaignApi) => {
            this.patchState({
              campaign: campaignResponse,
              campaignSubmitLoading: false,
              creatorsLoading: false,
            });
          }),
          catchError((err) =>
            this.handleSubmitError(err, {
              operation: 'createCampaign',
              payload: campaign,
              snackbarKey: 'pages.campaignForm.sections.couldNotBeCreated',
              timeoutMessage: 'Campaign create request timed out',
            }),
          ),
        ),
      ),
    );
  });

  public readonly updateCampaign = this.effect((trigger$: Observable<TCampaignUpdatePayload>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ campaignSubmitLoading: true, error: null })),
      switchMap((campaign: TCampaignUpdatePayload) =>
        this.campaignsService.updateCampaign(campaign.id, campaign).pipe(
          timeout(60_000),
          tap((campaignResponse: TCampaignApi) => {
            this.campaignDatesService.setStartDate(
              this.dateUtils.parseDateTimeFromApi(campaignResponse.start as string),
            );
            this.campaignDatesService.setEndDate(this.dateUtils.parseDateTimeFromApi(campaignResponse.end as string));

            this.patchState({
              campaign: campaignResponse,
              campaignSubmitLoading: false,
            });
          }),
          catchError((err) =>
            this.handleSubmitError(err, {
              campaignId: campaign.id,
              operation: 'updateCampaign',
              payload: campaign,
              snackbarKey: 'pages.campaignForm.sections.couldNotBeSaved',
              timeoutMessage: 'Campaign update request timed out',
            }),
          ),
        ),
      ),
    );
  });

  private readonly teamService = inject(TeamService);
  private readonly errorFormatterService = inject(ErrorFormatterService);

  constructor(
    private readonly campaignsService: CampaignsService,
    private readonly campaignDatesService: CampaignDatesService,
    private readonly dateUtils: DateUtilitiesService,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
  ) {
    super(initialState);
  }
}
